import Highcharts from 'highcharts';

import { InsightsChartSpec } from 'services/streamInsightsQuery';

import { baseInsightsOptions, formatChartValue } from './shared';

// The Highcharts series type behind each of our slice-shaped kinds. `donut` is a
// pie with a wider hole; `pyramid` and `funnel` both ship in the funnel module.
const HIGHCHARTS_TYPE: Record<string, string> = {
  pie: 'pie',
  donut: 'pie',
  funnel: 'funnel',
  pyramid: 'pyramid',
  packedBubble: 'packedbubble',
};

// Pie's hole. Donut leaves a wider centre — matching the saved gadget, which
// passes innerSize '65%' to usePieData against pie's default '50%'.
const INNER_SIZE: Record<string, string> = { pie: '55%', donut: '65%' };

// pie | donut | funnel | pyramid | packedBubble — single-series charts whose
// points carry their own name rather than sitting on a category axis. They have
// no axes at all, so (unlike the cartesian kinds) we must not attach any: an
// explicit `undefined` would clobber Highcharts' defaults.
const buildSliceOptions = (spec: InsightsChartSpec): Highcharts.Options => {
  const { chartType, formatting } = spec;
  const type = HIGHCHARTS_TYPE[chartType];
  const isPieLike = chartType === 'pie' || chartType === 'donut';
  const isFunnelLike = chartType === 'funnel' || chartType === 'pyramid';

  const values = spec.series[0]?.data ?? [];
  // Single-result kinds never actually receive nulls (their categories come
  // from the result's own rows), so the coalesce is for the type only.
  const points = values.map((y, index) => ({
    name: spec.categories[index] ?? '',
    y: y ?? 0,
  }));

  // Funnel and pyramid read top-to-bottom largest-to-smallest, same as the
  // saved gadget (useFunnelData sorts descending before rendering).
  const ordered = isFunnelLike ? [...points].sort((a, b) => b.y - a.y) : points;

  const dataLabels: Highcharts.DataLabelsOptions = {
    enabled: true,
    style: { color: 'var(--color-text-primary)' },
    formatter() {
      return `${this.point.name}: ${formatChartValue(this.y ?? 0, formatting)}`;
    },
  };

  const base = baseInsightsOptions({
    spec,
    chartType: type,
    usePointName: true,
    // A funnel/pyramid/packed bubble labels its own points, so a legend just
    // repeats them. Pie and donut keep theirs.
    showLegend: isPieLike,
  });

  return {
    ...base,
    series: [
      {
        type,
        name: spec.series[0]?.name,
        // Packed bubble sizes each bubble by `value`, not `y`.
        data:
          chartType === 'packedBubble'
            ? ordered.map(({ name, y }) => ({ name, value: y }))
            : ordered,
      },
    ] as unknown as Highcharts.SeriesOptionsType[],
    plotOptions: {
      ...base.plotOptions,
      ...(isPieLike
        ? { pie: { innerSize: INNER_SIZE[chartType], dataLabels } }
        : {}),
      ...(chartType === 'funnel' ? { funnel: { dataLabels } } : {}),
      ...(chartType === 'pyramid' ? { pyramid: { dataLabels } } : {}),
      ...(chartType === 'packedBubble'
        ? {
            packedbubble: {
              minSize: '30%',
              maxSize: '100%',
              dataLabels: {
                enabled: true,
                format: '{point.name}',
                style: {
                  color: 'var(--color-text-primary)',
                  textOutline: 'none',
                  fontWeight: 'normal',
                },
              },
            },
          }
        : {}),
    },
  };
};

export default buildSliceOptions;
